/* eslint-disable import/no-deprecated */
import { intl } from '@fullstory/intl';
import { classNames } from '@fullstory/util/fmt';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { Typography } from '../../deprecated/Typography';
import { Box } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { FSMaskProp } from '../../types';
import { identifyComponent } from '../../util';
import { Button } from '../Button';
import css from './index.module.scss';
import { copyText } from './utils';

type NativeTextAreaAttributes = Omit<React.ComponentPropsWithoutRef<'textarea'>, 'className'>;

export type TextAreaSize = 'x-small' | 'small' | 'medium' | 'large';
export type TextAreaVariant = 'fill' | 'outline';
export type TextAreaFont = 'mono' | 'serif' | 'sans';

export interface TextAreaProps extends NativeTextAreaAttributes {
  /** If `always`, `onHover`, or `affixed`, a button will appear in the textarea which will copy contents to clipboard. Defaults to `never`. */
  copyable?: 'never' | 'onHover' | 'always' | 'affixed';
  /** Overrides the default copy label ("Copy") */
  copyLabel?: string;
  /** Font type for the text area. Defaults to `sans` */
  fontFamily?: TextAreaFont;
  /** FS Mask rule */
  fsMask?: FSMaskProp;
  /** If `true`, the textarea will be marked as invalid */
  invalid?: boolean;
  /** Functions to call when user clicks the Copy button (successfully or not) */
  onCopyButtonClicked?: { success: () => void; error: () => void };
  /** If `true`, the standard textarea resize controls will be active */
  resizable?: boolean;
  /** Size of text area, default to `medium` */
  size?: TextAreaSize;
  /** For element identity and testing purposes. Defualts to `text-area` */
  testId?: string;
  /** Type of TextArea. Default is `fill` */
  variant?: TextAreaVariant;
}

/**
 * A text area is used to capture longer form text from users.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      copyable = 'never',
      copyLabel = intl.formatMessage({ defaultMessage: 'Copy', id: '4l6vz1' }),
      disabled = false,
      fontFamily = 'sans',
      fsMask = null,
      invalid,
      maxLength,
      onCopyButtonClicked,
      resizable = false,
      size = 'medium',
      testId = 'text-area',
      variant = 'fill',
      onChange,
      value,
      ...rest
    },
    ref,
  ) => {
    const [currentLength, setCurrentLength] = useState(0);
    const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => setCurrentLength(String(value).length), [value]);
    return (
      <div
        data-component="TextArea"
        className={classNames(css.container, {
          [css[size]]: !!size,
        })}
      >
        <textarea
          aria-invalid={invalid}
          className={classNames(fsMask, css.textArea, css[fontFamily], {
            [css.invalid]: !!invalid,
            [css[variant]]: !!variant,
            [css.disabled]: !!disabled,
            [css.resizable]: !!resizable,
            [css.copyAffixed]: copyable === 'affixed',
          })}
          data-testid={testId}
          disabled={disabled}
          ref={node => {
            if (ref) {
              if (typeof ref === 'function') {
                ref(node);
              } else {
                ref.current = node;
              }
            }
            textAreaRef.current = node;
          }}
          onChange={e => {
            setCurrentLength(e.target.value.length);
            onChange?.(e);
          }}
          maxLength={maxLength}
          value={value}
          {...rest}
          {...identifyComponent('text-area', rest)}
        />
        {maxLength && (
          <Box padding={4}>
            <Flex justifyContent="flex-end">
              <Typography variant="heading8" color="neutrals-n7">
                {`${currentLength}/${maxLength}`}
              </Typography>
            </Flex>
          </Box>
        )}
        {copyable === 'affixed' && (
          <div className={classNames(css.copyButton, css.affixed)}>
            <Button
              variant="outline"
              iconLeft="copy"
              onClick={() => {
                if (copyText(textAreaRef)) {
                  onCopyButtonClicked?.success();
                } else {
                  onCopyButtonClicked?.error();
                }
              }}
              size="small"
              fullWidth
              justifyContent="center"
            >
              {copyLabel}
            </Button>
          </div>
        )}
        {(copyable === 'always' || copyable === 'onHover') && (
          <Button
            variant={copyable === 'onHover' ? 'outline' : 'minimal'}
            iconLeft="copy"
            onClick={() => {
              if (copyText(textAreaRef)) {
                onCopyButtonClicked?.success();
              } else {
                onCopyButtonClicked?.error();
              }
            }}
            className={classNames(css.copyButton, {
              [css.always]: copyable === 'always',
              [css.onHover]: copyable === 'onHover',
            })}
          >
            {copyLabel}
          </Button>
        )}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
